import * as B from '@blueprintjs/core';
import {DateRange} from '@blueprintjs/datetime2';
import moment from 'moment';
import React from 'react';
import {FilterProps as ReactTableFilterProps} from 'react-table';

import List, {ListItem} from 'app/components/List';
import {ApiProjectReport} from 'app/modules/Remote/Feature';
import colors from 'app/styles/colors.json';

import cs from '../PropertyOverview.module.scss';
import {isFilterActive} from '../tableUrlState';
import {
  decodeDateRange,
  decodeIdList,
  encodeDateRange,
  encodeIdList,
  isYearDateRange,
  reportAuthors,
  reportProperties,
  reportTypes,
  reportYears,
  toggleId,
  yearDateRange,
} from './reportsTableState';

const ALL_ID = 'all';
const CUSTOM_RANGE_ID = 'custom';

// Header filter button styled like the property table's column filters. Rendering
// without a portal keeps the popover inside the header cell, which is what keeps
// the hover-revealed filter button visible while the popover is open.
const ColumnFilterPopover: React.FunctionComponent<{
  isActive: boolean;
  content: React.JSX.Element;
}> = ({isActive, content}) => (
  <B.Popover position={B.Position.BOTTOM} usePortal={false} content={content}>
    <B.Button
      minimal
      active={isActive}
      icon={<B.Icon icon="filter" color={colors.darkestGray} />}
      small
    />
  </B.Popover>
);

/** Picks one of a column's values, or all of them. */
export const SelectColumnFilter: React.FunctionComponent<{
  allText: string;
  options: {id: string; name: string}[];
  value: string | undefined;
  setValue: (value: string | undefined) => void;
}> = ({allText, options, value, setValue}) => {
  const isActive = isFilterActive(value);

  return (
    <ColumnFilterPopover
      isActive={isActive}
      content={
        <List
          className={cs.actionMenu}
          items={[
            {id: ALL_ID, text: allText, isSelected: !isActive},
            {id: 'sep', text: '', isSeparator: true},
            ...options.map((option) => ({
              id: option.id,
              text: option.name,
              isSelected: option.id === value,
            })),
          ]}
          onClickItem={({id}) => setValue(id === ALL_ID ? undefined : id)}
        />
      }
    />
  );
};

export const TypeColumnFilter: React.FunctionComponent<ReactTableFilterProps<ApiProjectReport>> = ({
  column,
  setFilter,
  data,
}) => {
  const types = React.useMemo(() => reportTypes(data), [data]);

  return (
    <SelectColumnFilter
      allText="All types"
      options={types.map((type) => ({id: type, name: type}))}
      value={column.filterValue}
      setValue={(value) => setFilter(column.id, value)}
    />
  );
};

export interface MultiSelectFilterProps {
  allText: string;
  // Names the options in a summary like "3 properties".
  pluralNoun: string;
  options: {id: string; name: string}[];
  value: unknown;
  setValue: (value: string | undefined) => void;
}

/** Ticks any number of a column's values; no ticks means all of them. */
const MultiSelectList: React.FunctionComponent<MultiSelectFilterProps> = ({
  allText,
  options,
  value,
  setValue,
}) => {
  const selectedIds = decodeIdList(value);
  const tick = (isSelected: boolean): B.IconName => (isSelected ? 'tick' : 'blank');

  const items: ListItem<{id: string}>[] = [
    {id: ALL_ID, text: allText, icon: tick(!selectedIds.length)},
    {id: 'sep', text: '', isSeparator: true},
    ...options.map((option) => ({
      id: option.id,
      text: option.name,
      icon: tick(selectedIds.includes(option.id)),
    })),
  ];

  return (
    <List
      className={cs.actionMenu}
      items={items}
      onClickItem={({id}) =>
        setValue(id === ALL_ID ? undefined : encodeIdList(toggleId(selectedIds, id)))
      }
    />
  );
};

function selectionSummary({allText, pluralNoun, options, value}: MultiSelectFilterProps): string {
  const selectedIds = decodeIdList(value);
  if (!selectedIds.length) {
    return allText;
  }
  if (selectedIds.length === 1) {
    return options.find((option) => option.id === selectedIds[0])?.name ?? allText;
  }
  return `${selectedIds.length} ${pluralNoun}`;
}

/** The multi-select list behind a column header's filter button. */
const MultiSelectColumnFilter: React.FunctionComponent<MultiSelectFilterProps> = (props) => (
  <ColumnFilterPopover
    isActive={decodeIdList(props.value).length > 0}
    content={<MultiSelectList {...props} />}
  />
);

/** The multi-select list as a labelled dropdown, for the toolbar's filter panel. */
export const MultiSelectFilterButton: React.FunctionComponent<MultiSelectFilterProps> = (props) => (
  <B.Popover
    position={B.Position.BOTTOM_LEFT}
    // The panel is itself a popover, so keep a click in here from closing it.
    captureDismiss
    content={<MultiSelectList {...props} />}
  >
    <B.Button fill alignText="left" rightIcon="caret-down" text={selectionSummary(props)} />
  </B.Popover>
);

export const PropertyColumnFilter: React.FunctionComponent<
  ReactTableFilterProps<ApiProjectReport>
> = ({column, setFilter, data}) => {
  const properties = React.useMemo(() => reportProperties(data), [data]);

  return (
    <MultiSelectColumnFilter
      allText="All properties"
      pluralNoun="properties"
      options={properties}
      value={column.filterValue}
      setValue={(value) => setFilter(column.id, value)}
    />
  );
};

export const AuthorColumnFilter: React.FunctionComponent<
  ReactTableFilterProps<ApiProjectReport>
> = ({column, setFilter, data}) => {
  const authors = React.useMemo(() => reportAuthors(data), [data]);

  return (
    <MultiSelectColumnFilter
      allText="All authors"
      pluralNoun="authors"
      options={authors}
      value={column.filterValue}
      setValue={(value) => setFilter(column.id, value)}
    />
  );
};

function formatDateRange([start, end]: DateRange): string {
  const format = (date: Date | null) => (date ? moment(date).format('L') : '…');
  return `${format(start)} – ${format(end)}`;
}

export const CreatedColumnFilter: React.FunctionComponent<
  ReactTableFilterProps<ApiProjectReport>
> = ({column, setFilter, data}) => {
  const range = decodeDateRange(column.filterValue);
  const isActive = !!(range[0] || range[1]);
  const years = React.useMemo(() => reportYears(data), [data]);

  const items: ListItem<{id: string}>[] = [
    {id: ALL_ID, text: 'All dates', isSelected: !isActive},
    {id: 'sep', text: '', isSeparator: true},
    ...years.map((year) => ({
      id: String(year),
      text: String(year),
      isSelected: isYearDateRange(range, year),
    })),
  ];
  // A custom range comes from the toolbar's date inputs; show it so the active
  // filter is legible here, even though this menu only offers whole years.
  if (isActive && !years.some((year) => isYearDateRange(range, year))) {
    items.push(
      {id: 'sep2', text: '', isSeparator: true},
      {id: CUSTOM_RANGE_ID, text: formatDateRange(range), isSelected: true, isDisabled: true}
    );
  }

  return (
    <ColumnFilterPopover
      isActive={isActive}
      content={
        <List
          className={cs.actionMenu}
          items={items}
          onClickItem={({id}) => {
            if (id === ALL_ID) {
              setFilter(column.id, undefined);
            } else if (id !== CUSTOM_RANGE_ID) {
              setFilter(column.id, encodeDateRange(yearDateRange(Number(id))));
            }
          }}
        />
      }
    />
  );
};
